import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  effect,
  HostListener,
  Renderer2,
  Inject,
  AfterViewChecked,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TournamentService } from '../../services/tournament/tournament.service';
import { ConfigService } from '../../services/config/config.service';
import { AuthService } from '../../services/auth/auth.service';
import { SimulationContextService } from '../../services/shared/simulation-context.service';
import { Round } from '../../models/round';
import { SimulatorService } from '../../services/simulator/simulator.service';
import { ScorecardService } from '../../services/scorecard/scorecard.service';
import { PairingsService } from '../../services/pairings/pairings.service';
import { DialogService } from '../../services/shared/dialog/dialog.service';
import { LegacyReplayTemplateSelectorComponent } from '../legacy-replay-template-selector/legacy-replay-template-selector.component';
import { LegacyReplayTemplateService } from '../../services/simulator/legacy-replay-template.service';
import { ModulesTournamentsApplicationTournamentsTournamentResponse } from '../../api-client/model/modulesTournamentsApplicationTournamentsTournamentResponse';
import { HttpClient } from '@angular/common/http';
import { Subscription, firstValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import {
  SimulationDefinitionDto,
  SimulationExecutionDto,
  SimulationExecutionProgressDto,
  SimulationExecutionSummaryDto,
  SimulationProfile,
  SimulationType,
  CreateSimulationDefinitionRequest,
  UpdateSimulationDefinitionRequest,
  LegacyReplayTemplate,
  RoundValidationResult,
} from '../../models/simulator.models';


interface DefinitionForm {
  name: string;
  description?: string;
  profileName?: string;
  useMockTournament: boolean;
  sourceTournamentId: string;
  vNextTournamentId?: string;
  legacyServer: string;
  legacyDatabase: string;
  legacyUserId: string;
  legacyPassword: string;
  seed?: number;
  createdBy: string;
  // Continuous Execution settings
  continuousExecution?: boolean;
  autoCloseRounds?: boolean;
  autoMarkOfficial?: boolean;
  skipRoundValidation?: boolean;
  transitionDelaySeconds?: number;
}

@Component({
  selector: 'app-simulator',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    LegacyReplayTemplateSelectorComponent,
  ],
  templateUrl: './simulator.component.html',
  styleUrls: ['./simulator.component.scss'],
})
export class SimulatorComponent implements OnInit, OnDestroy, AfterViewChecked {
  // Basic setup - values set from config in ngOnInit
  simulatorSwaggerUrl = '';
  simulatorBasePath = '';
  defaultLegacyServer = '';

  // Component state (using signals)
  simulationType = signal<SimulationType>('mock');
  hasActiveTournament = signal<boolean>(false);
  simulatorStatus = signal<'unknown' | 'online' | 'offline'>('unknown');
  checkingStatus = signal<boolean>(false);

  // Definition management
  definitions = signal<SimulationDefinitionDto[]>([]);
  selectedDefinition = signal<SimulationDefinitionDto | null>(null);
  selectedLegacyTemplate = signal<LegacyReplayTemplate | null>(null);
  isCreatingDefinition = signal<boolean>(false);
  profiles = signal<SimulationProfile[]>([]);
  // Profiles that are not compatible with mock tournament mode
  private readonly mockIncompatibleProfiles = ['TimeAwareReplay'];
  isDefinitionModalOpen = signal<boolean>(false);
  isEditingDefinition = signal<boolean>(false);
  // Active tournament info (must be declared before definitionForm since buildDefaultForm uses it)
  activeTournamentId = signal<string | null>(null);
  activeTournamentName = signal<string | null>(null);
  activeTournamentRounds = signal<Round[]>([]);
  availableRoundNumbers = signal<number[]>([]);
  definitionForm = signal<DefinitionForm>(this.buildDefaultForm());
  formError = signal<string | null>(null);

  // Execution management
  currentExecution = signal<SimulationExecutionDto | null>(null);
  executionProgress = signal<SimulationExecutionProgressDto | null>(null);
  maxProgressPercentageReached = signal<number>(0); // Track highest progress seen to enforce monotonic increase
  executionHistory = signal<SimulationExecutionDto[]>([]); // All executions for history view
  currentExecutionDefinitionName = computed(() => {
    const defId = this.currentExecution()?.definitionId;
    if (!defId) return null;

    const fromList = this.definitions().find((d) => d.id === defId);
    if (fromList) return fromList.name;

    const selected = this.selectedDefinition();
    return selected?.id === defId ? selected.name : null;
  });
  isExecutionRunning = computed(
    () =>
      this.currentExecution()?.status === 'Running' ||
      this.currentExecution()?.status === 'Paused'
  );

  // UI state
  activeTab = signal<'definitions' | 'execute' | 'history'>('definitions');
  summaryActiveTab = signal<
    | 'metadata'
    | 'performance'
    | 'errors'
    | 'rounds'
    | 'time-aware'
    | 'validation'
  >('metadata');
  showValidationResults = signal<boolean>(false);
  isExecuteModalOpen = signal<boolean>(false);
  executeDefinitionId = signal<string | null>(null);
  executeStartRound = signal<number>(1);
  isDryRunMode = signal<boolean>(false);
  isSummaryModalOpen = signal<boolean>(false);
  summaryLoading = signal<boolean>(false);
  summaryError = signal<string | null>(null);
  executionSummary = signal<SimulationExecutionSummaryDto | null>(null);
  isValidationModalOpen = signal<boolean>(false);
  validationResult = signal<RoundValidationResult | null>(null);
  isLoadingTournamentId = false;
  // Collapsible section state for small screens
  isLegacySectionCollapsed = signal<boolean>(false);  // Legacy Tournament Setup: expanded by default
  isContinuousSectionCollapsed = signal<boolean>(true);  // Continuous Execution: collapsed by default
  // Connection status for legacy database: 'idle' | 'loading' | 'connected' | 'failed'
  legacyConnectionStatus = signal<'idle' | 'loading' | 'connected' | 'failed'>('idle');
  legacyConnectionError = signal<string | null>(null);
  isResettingRound = signal<boolean>(false);
  isRestoringDatabase = signal<boolean>(false);

  // Continuous execution / round transition state
  isProcessingRoundTransition = signal<boolean>(false);
  roundTransitionMessage = signal<string | null>(null);

  // Subscriptions
  private subscriptions = new Subscription();

  // Modal portal tracking
  private modalMovedToBody = false;

  constructor(
    private tournamentsService: TournamentService,
    private scorecardService: ScorecardService,
    private pairingsService: PairingsService,
    private configService: ConfigService,
    private legacyReplayTemplateService: LegacyReplayTemplateService,
    private simulatorService: SimulatorService,
    private simulationContextService: SimulationContextService,
    private http: HttpClient,
    private toastr: ToastrService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    private authService: AuthService,
    private dialogService: DialogService
  ) {
  }

  ngAfterViewChecked(): void {
    // Move modal elements to body when modal is open (escapes CSS containment)
    if (this.isDefinitionModalOpen() && !this.modalMovedToBody) {
      this.moveModalToBody();
    }
  }

  private moveModalToBody(): void {
    const backdrop = this.document.querySelector('.sim-modal-backdrop');
    const modal = this.document.querySelector('.sim-modal');
    if (backdrop && modal) {
      this.renderer.appendChild(this.document.body, backdrop);
      this.renderer.appendChild(this.document.body, modal);
      this.modalMovedToBody = true;
    }
  }

  private removeModalFromBody(): void {
    const backdrop = this.document.querySelector('.sim-modal-backdrop');
    const modal = this.document.querySelector('.sim-modal');
    if (backdrop) {
      this.renderer.removeChild(this.document.body, backdrop);
    }
    if (modal) {
      this.renderer.removeChild(this.document.body, modal);
    }
    this.modalMovedToBody = false;
  }

  private buildDefaultForm(): DefinitionForm {
    return {
      name: '',
      description: '',
      profileName: 'RealisticPacing',
      useMockTournament: false, // Default to Legacy Replay
      sourceTournamentId: '',
      vNextTournamentId: this.activeTournamentId() || '',
      legacyServer: '',
      legacyDatabase: '',
      legacyUserId: 'sa',
      legacyPassword: 'Password123',
      seed: undefined, // Optional seed for Mock mode
      createdBy: 'admin',
      // Continuous Execution defaults
      continuousExecution: false,
      autoCloseRounds: true,
      autoMarkOfficial: true,
      skipRoundValidation: false,
      transitionDelaySeconds: 30,
    };
  }

  ngOnInit(): void {
    this.checkActiveTournament();
    const basePath = this.configService.config?.simulatorBasePath || '';
    this.simulatorBasePath = basePath;
    this.simulatorSwaggerUrl = basePath ? `${basePath}/swagger` : '';
    this.defaultLegacyServer = this.configService.config?.defaultLegacyServer || '';
    this.checkSimulatorStatus();
    this.loadDefinitions();
    this.loadProfiles();
    this.loadActiveTournamentId();
    this.checkForActiveExecution();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.simulatorService.stopProgressPolling();
    // Clean up modal elements if component is destroyed while modal is open
    if (this.modalMovedToBody) {
      this.removeModalFromBody();
      this.unlockBodyScroll();
    }
  }

  // Close modal with Escape key for accessibility and UX
  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent | Event): void {
    if (this.isDefinitionModalOpen()) {
      event.preventDefault();
      this.cancelDefinitionForm();
    }
  }

  // ===== Navigation =====

  openSwagger(): void {
    window.open(this.simulatorSwaggerUrl, '_blank');
  }

  setSimulationType(type: SimulationType): void {
    this.simulationType.set(type);
  }

  setActiveTab(tab: 'definitions' | 'execute' | 'history'): void {
    this.activeTab.set(tab);
  }

  refreshStatus(): void {
    this.checkActiveTournament();
    this.checkSimulatorStatus();
    this.loadDefinitions();
  }

  private refreshSimulatorState(): void {
    this.simulatorService.stopProgressPolling();
    this.currentExecution.set(null);
    this.executionProgress.set(null);
    this.maxProgressPercentageReached.set(0);
    this.executionHistory.set([]);
    this.selectedDefinition.set(null);
    this.refreshStatus();
    this.refreshExecutionHistory();
  }

  // ===== Definition Management =====

  private loadDefinitions(): void {
    this.subscriptions.add(
      this.simulatorService.getAllDefinitions().subscribe({
        next: (definitions) => {
          this.definitions.set(definitions);
        },
        error: (err) => {
          console.error('Failed to load definitions:', err);
        },
      })
    );
  }

  private loadProfiles(): void {
    this.subscriptions.add(
      this.simulatorService.getProfiles().subscribe({
        next: (profiles) => {
          this.profiles.set(profiles);
          if (!profiles || profiles.length === 0) {
            this.toastr.warning(
              'No profiles returned by Simulator API',
              'Profiles'
            );
          }
        },
        error: (err) => {
          console.error('Failed to load profiles:', err);
          this.toastr.error('Failed to load profiles from Simulator', 'Error');
        },
      })
    );
  }

  filteredProfiles = computed(() => {
    const list = this.profiles();
    const useMock = this.definitionForm().useMockTournament;
    const filtered = useMock
      ? list.filter((p) => !this.mockIncompatibleProfiles.includes(p.name))
      : list;
    // Sort from slowest to fastest by approximateSpeedup (ascending)
    return [...filtered].sort((a, b) => {
      const speedA = a.timing?.approximateSpeedup ?? 0;
      const speedB = b.timing?.approximateSpeedup ?? 0;
      return speedA - speedB;
    });
  });

  // Helper to resolve currently selected profile for summary display
  selectedProfile = computed(() => {
    const name = this.definitionForm().profileName;
    const list = this.profiles();
    if (!name || !Array.isArray(list)) return null as any;
    return list.find((p) => p.name === name) || null;
  });

  private checkForActiveExecution(): void {
    // Check if there's a running or pending execution and restore state
    this.subscriptions.add(
      this.simulatorService.getAllExecutions().subscribe({
        next: (executions) => {
          // Store all executions in history (sorted by start date, newest first)
          const sortedExecs = [...executions].sort((a, b) => {
            const dateA = a.startedAt ? new Date(a.startedAt).getTime() : 0;
            const dateB = b.startedAt ? new Date(b.startedAt).getTime() : 0;
            return dateB - dateA; // Descending order
          });
          this.executionHistory.set(sortedExecs);

          // Find first Running, Pending, or Paused execution (all are "active" and resumable)
          const activeExec = executions.find(
            (e) => e.status === 'Running' || e.status === 'Pending' || e.status === 'Paused'
          );
          if (activeExec) {
            // Found an active execution, restore state
            this.currentExecution.set(activeExec);
            this.activeTab.set('execute');

            // Load the definition for this execution (needed for continuous execution round transitions)
            this.loadDefinitionForExecution(activeExec.definitionId);

            // Start active progress monitoring (only if Running or Pending; Paused doesn't need polling)
            if (activeExec.status === 'Running' || activeExec.status === 'Pending') {
              this.startProgressMonitoring(activeExec.id);
            } else {
              // For Paused, just load the current progress without polling
              this.loadProgressOnce(activeExec.id);
            }
          }
        },
        error: (err) => {
          console.error('Failed to check for active executions:', err);
        },
      })
    );
  }

  /**
   * Load the definition associated with an execution.
   * This is needed when restoring an active execution (e.g., on page refresh)
   * to ensure the definition's vNextTournamentId is available for round transitions.
   */
  private loadDefinitionForExecution(definitionId: string): void {
    this.subscriptions.add(
      this.simulatorService.getDefinitionById(definitionId).subscribe({
        next: (definition) => {
          this.selectedDefinition.set(definition);
        },
        error: (err) => {
          console.error('Failed to load definition for execution:', err);
        }
      })
    );
  }

  selectDefinition(definition: SimulationDefinitionDto): void {
    this.selectedDefinition.set(definition);
  }

  createNewDefinition(): void {
    this.isCreatingDefinition.set(true);
    this.isEditingDefinition.set(false);
    this.formError.set(null);
    // Reset connection status for new definition
    this.legacyConnectionStatus.set('idle');
    this.legacyConnectionError.set(null);
    const defaultForm = this.buildDefaultForm();
    if (this.activeTournamentId()) {
      defaultForm.vNextTournamentId = this.activeTournamentId()!;
    }
    this.definitionForm.set(defaultForm);
    this.selectedDefinition.set(null);
    this.selectedLegacyTemplate.set(null);

    // Auto-select Legacy Template based on active tournament name
    if (this.activeTournamentName() && !defaultForm.useMockTournament) {
      this.subscriptions.add(
        this.legacyReplayTemplateService.getActiveTemplates().subscribe({
          next: (templates) => {
            const activeName = this.activeTournamentName()!.toLowerCase();
            const match = templates.find(t => {
              const templateName = t.templateName.toLowerCase();
              // Try exact match first, then bidirectional substring match
              return templateName === activeName ||
                templateName.includes(activeName) ||
                activeName.includes(templateName);
            });
            if (match) {
              this.selectedLegacyTemplate.set(match);
              this.onLegacyTemplateSelected(match);
            }
          },
          error: (err) => {
            console.error('Failed to load templates for auto-selection:', err);
          }
        })
      );
    }

    this.isDefinitionModalOpen.set(true);
    this.lockBodyScroll();
    this.scrollModalToTop();
  }

  cancelDefinitionForm(): void {
    this.isCreatingDefinition.set(false);
    this.isDefinitionModalOpen.set(false);
    this.formError.set(null);
    this.selectedDefinition.set(null);
    this.selectedLegacyTemplate.set(null); // Clear template selection
    // Reset connection status
    this.legacyConnectionStatus.set('idle');
    this.legacyConnectionError.set(null);
    this.removeModalFromBody();
    this.unlockBodyScroll();
  }

  editDefinition(definition: SimulationDefinitionDto): void {
    this.isCreatingDefinition.set(true);
    this.isEditingDefinition.set(true);
    this.formError.set(null);
    // Reset connection status for editing
    this.legacyConnectionStatus.set('idle');
    this.legacyConnectionError.set(null);
    this.selectedDefinition.set(definition);
    this.definitionForm.set(this.mapDefinitionToForm(definition));
    this.selectedLegacyTemplate.set(null);

    // Match template by database name for Legacy Replay definitions
    if (!definition.useMockTournament && definition.legacyConnection?.database) {
      this.subscriptions.add(
        this.legacyReplayTemplateService.getActiveTemplates().subscribe({
          next: (templates) => {
            const match = templates.find(
              (t) => t.databaseName === definition.legacyConnection?.database
            );
            if (match) {
              this.selectedLegacyTemplate.set(match);
            }
          },
          error: (err) => {
            console.error('Failed to load templates for edit:', err);
          },
        })
      );
    }

    this.isDefinitionModalOpen.set(true);
    this.lockBodyScroll();
    this.scrollModalToTop();
  }

  saveDefinition(): void {
    const form = this.definitionForm();
    const effectiveName =
      form.name || (form.useMockTournament ? 'Mock Simulation' : '');

    // Validate profile is required
    if (!form.profileName) {
      this.formError.set(
        'Profile is required. Please choose a simulation profile.'
      );
      return;
    }

    // Mock mode validation
    if (form.useMockTournament) {
      if (!form.vNextTournamentId) {
        this.formError.set('vNext Tournament ID is required for Mock mode.');
        return;
      }
      // Validate GUID format
      const guidRegex = /^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$/;
      if (!guidRegex.test(form.vNextTournamentId)) {
        this.formError.set('vNext Tournament ID must be a valid GUID.');
        return;
      }
    }

    // Legacy replay validation
    if (!form.useMockTournament && (!form.name || !form.sourceTournamentId)) {
      this.formError.set('Name and source tournament are required for Legacy Replay.');
      return;
    }

    const groupAgentConfigs: { groupId: string }[] = [];

    const requestBase: CreateSimulationDefinitionRequest = {
      name: effectiveName,
      description: form.description,
      profileName: form.profileName,
      useMockTournament: form.useMockTournament,
      vNextTournamentId: form.vNextTournamentId || undefined,
      seed: form.useMockTournament ? form.seed : undefined,
      groupAgentConfigs,
      options: undefined,
      // In profile-only mode, omit rateLimiting to use profile defaults on server
      rateLimiting: undefined,
      // Legacy Replay specific settings
      ...(!form.useMockTournament
        ? {
          sourceTournamentId: form.sourceTournamentId,
          legacyServer: form.legacyServer,
          legacyDatabase: form.legacyDatabase,
          legacyUserId: form.legacyUserId,
          legacyPassword: form.legacyPassword,
        }
        : {}),
      // Continuous Execution settings (both modes)
      continuousExecution: form.continuousExecution ?? false,
      autoCloseRounds: form.continuousExecution ? (form.autoCloseRounds ?? true) : undefined,
      autoMarkOfficial: form.continuousExecution ? (form.autoMarkOfficial ?? true) : undefined,
      skipRoundValidation: form.continuousExecution ? (form.skipRoundValidation ?? false) : undefined,
      transitionDelaySeconds: form.continuousExecution ? (form.transitionDelaySeconds ?? 30) : undefined,
    };

    if (this.isEditingDefinition() && this.selectedDefinition()) {
      const updateReq: UpdateSimulationDefinitionRequest = {
        name: effectiveName,
        description: form.description,
        profileName: form.profileName,
        seed: form.useMockTournament ? form.seed : undefined,
        groupAgentConfigs,
        options: undefined,
        // Omit rateLimiting for updates to respect selected profile
        rateLimiting: undefined,
        // Continuous Execution settings
        continuousExecution: form.continuousExecution ?? false,
        autoCloseRounds: form.continuousExecution ? (form.autoCloseRounds ?? true) : undefined,
        autoMarkOfficial: form.continuousExecution ? (form.autoMarkOfficial ?? true) : undefined,
        skipRoundValidation: form.continuousExecution ? (form.skipRoundValidation ?? false) : undefined,
        transitionDelaySeconds: form.continuousExecution ? (form.transitionDelaySeconds ?? 30) : undefined,
        // Legacy connection overrides
        ...(!form.useMockTournament
          ? {
            legacyServer: form.legacyServer,
            legacyDatabase: form.legacyDatabase,
            legacyUserId: form.legacyUserId,
            legacyPassword: form.legacyPassword,
          }
          : {}),
      };

      this.subscriptions.add(
        this.simulatorService
          .updateDefinition(this.selectedDefinition()!.id, updateReq)
          .subscribe({
            next: (updated) => {
              this.isDefinitionModalOpen.set(false);
              this.isCreatingDefinition.set(false);
              this.selectedDefinition.set(updated);
              this.loadDefinitions();
              this.removeModalFromBody();
              this.unlockBodyScroll();
            },
            error: (err) => {
              console.error('Failed to update definition:', err);
              this.formError.set(
                'Failed to update definition. See console for details.'
              );
            },
          })
      );
      return;
    }

    const createReq: CreateSimulationDefinitionRequest = {
      ...requestBase,
      createdBy: this.authService.userName() || 'admin',
    };

    this.subscriptions.add(
      this.simulatorService.createDefinition(createReq).subscribe({
        next: (created) => {
          this.isDefinitionModalOpen.set(false);
          this.isCreatingDefinition.set(false);
          this.selectedDefinition.set(created);
          this.loadDefinitions();
          this.removeModalFromBody();
          this.unlockBodyScroll();
        },
        error: (err) => {
          console.error('Failed to create definition:', err);
          this.formError.set(
            'Failed to create definition. See console for details.'
          );
        },
      })
    );
  }

  private mapDefinitionToForm(def: SimulationDefinitionDto): DefinitionForm {
    return {
      name: def.name,
      description: def.description,
      profileName: def.profileName,
      useMockTournament: def.useMockTournament,
      sourceTournamentId: def.sourceTournamentId,
      vNextTournamentId: def.vNextTournamentId,
      legacyServer: def.legacyConnection?.server || '',
      legacyDatabase: def.legacyConnection?.database || '',
      legacyUserId: def.legacyConnection?.userId || '',
      legacyPassword: def.legacyConnection?.password || '',
      seed: def.seed,
      createdBy: def.createdBy || 'admin',
      // Continuous Execution settings
      continuousExecution: def.continuousExecution ?? false,
      autoCloseRounds: def.autoCloseRounds ?? true,
      autoMarkOfficial: def.autoMarkOfficial ?? true,
      skipRoundValidation: def.skipRoundValidation ?? false,
      transitionDelaySeconds: def.transitionDelaySeconds ?? 30,
    };
  }

  updateForm(patch: Partial<DefinitionForm>): void {
    const next = { ...this.definitionForm(), ...patch };
    if (patch.useMockTournament) {
      const profileName = next.profileName;
      if (profileName && !this.isProfileAllowed(profileName, true)) {
        next.profileName = '';
      }
    }
    this.definitionForm.set(next);
  }

  private isProfileAllowed(profileName: string, useMockTournament: boolean): boolean {
    if (!useMockTournament) return true;
    return !this.mockIncompatibleProfiles.includes(profileName);
  }

  /**
   * Handle legacy replay template selection
   */
  onLegacyTemplateSelected(template: LegacyReplayTemplate | null): void {
    this.selectedLegacyTemplate.set(template);
    // Reset connection status when template changes
    this.legacyConnectionStatus.set('idle');
    this.legacyConnectionError.set(null);
    if (template) {
      // Auto-populate legacy database from template; preserve existing server if set
      const currentServer = this.definitionForm().legacyServer;
      this.updateForm({
        legacyDatabase: template.databaseName,
        // Only set default server if not already specified (uses config defaultLegacyServer)
        ...(currentServer ? {} : { legacyServer: this.defaultLegacyServer }),
      });

      // Fetch the Tournament ID from the legacy database
      this.fetchTournamentIdFromLegacyDb();
    }
  }

  /**
   * Refresh/fetch the tournament ID from the legacy database.
   * Uses current form values for server, database, and credentials.
   * Can be called when user edits server or database fields.
   */
  refreshTournamentId(): void {
    const form = this.definitionForm();
    if (!form.legacyDatabase) {
      this.toastr.warning('Please select a legacy database first.', 'Missing Database');
      return;
    }
    this.fetchTournamentIdFromLegacyDb();
  }

  /**
   * Internal method to fetch the tournament ID from the legacy database.
   * Uses current form values for server, database, and credentials.
   */
  private fetchTournamentIdFromLegacyDb(): void {
    const form = this.definitionForm();
    const legacyServer = form.legacyServer || this.defaultLegacyServer;
    const legacyDatabase = form.legacyDatabase;
    const legacyUserId = form.legacyUserId || 'sa';
    const legacyPassword = form.legacyPassword || 'Password123';

    if (!legacyDatabase) {
      return;
    }

    // Fetch the Tournament ID from the legacy database
    this.isLoadingTournamentId = true;
    this.legacyConnectionStatus.set('loading');
    this.legacyConnectionError.set(null);
    const request = {
      legacyServer,
      legacyDatabase,
      legacyUserId,
      legacyPassword,
    };

    this.subscriptions.add(
      this.legacyReplayTemplateService.getTournamentId(request).subscribe({
        next: (response) => {
          this.isLoadingTournamentId = false;
          this.legacyConnectionStatus.set('connected');
          this.legacyConnectionError.set(null);
          this.updateForm({
            sourceTournamentId: response.tournamentId,
          });
        },
        error: (error) => {
          this.isLoadingTournamentId = false;
          this.legacyConnectionStatus.set('failed');
          const errorMessage = `Failed to connect to ${legacyDatabase} on ${legacyServer}`;
          this.legacyConnectionError.set(errorMessage);
          console.error(
            'Failed to fetch tournament ID from legacy database:',
            error
          );
          this.toastr.error(
            `Failed to query tournament ID from ${legacyDatabase} on ${legacyServer}. Please verify the database exists and credentials are correct.`,
            'Error'
          );
        },
      })
    );
  }

  // Rate limiting removed from Admin UI; profile presets are used server-side

  private lockBodyScroll(): void {
    // Prevent background page from scrolling while modal is open
    document.body.style.overflow = 'hidden';
    (document.documentElement as HTMLElement).style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    document.body.style.overflow = '';
    (document.documentElement as HTMLElement).style.overflow = '';
  }

  async deleteDefinition(id: string): Promise<void> {
    const definition = this.definitions().find((d) => d.id === id);
    const definitionName = definition?.name || 'this simulation definition';
    const confirmed = await this.dialogService.confirm(
      `Are you sure you want to delete "${definitionName}"?`,
      {
        title: 'Delete Simulation Definition',
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
        danger: true
      }
    );

    if (!confirmed) {
      return;
    }

    this.subscriptions.add(
      this.simulatorService.deleteDefinition(id).subscribe({
        next: () => {
          // Optimistically update UI by filtering out the deleted definition
          const currentDefinitions = this.definitions();
          this.definitions.set(currentDefinitions.filter((d) => d.id !== id));

          // Clear selection if this definition was selected
          if (this.selectedDefinition()?.id === id) {
            this.selectedDefinition.set(null);
          }

          this.toastr.success('Definition deleted successfully', 'Success');

          // Refresh list from server to ensure consistency
          this.loadDefinitions();
        },
        error: (err) => {
          console.error('Failed to delete definition:', err);

          // Check for specific error responses
          if (err.status === 409) {
            this.toastr.error(
              'Cannot delete definition. It has an active or pending execution.',
              'Delete Failed'
            );
          } else if (err.status === 404) {
            this.toastr.error('Definition not found.', 'Delete Failed');
          } else {
            this.toastr.error(
              err.error?.message ||
              'Failed to delete definition. See console for details.',
              'Error'
            );
          }

          // Refresh the list to ensure UI is in sync with server
          this.loadDefinitions();
        },
      })
    );
  }

  // ===== Execution Management =====

  openExecuteModal(definitionId: string): void {
    this.executeDefinitionId.set(definitionId);
    this.isDryRunMode.set(false);
    // Default to the active round from the tournament context or next round
    const selectedRound = this.tournamentsService.selectedRoundSignal();
    const rounds = this.activeTournamentRounds();

    // Find the active round in the tournament
    const activeRound = rounds.find((r) => r.active);

    if (activeRound) {
      // If there's an active round, use it
      this.executeStartRound.set(activeRound.number ?? 1);
    } else if (selectedRound?.number) {
      // If no active round but there's a selected round, use it
      this.executeStartRound.set(selectedRound.number);
    } else if (rounds.length > 0) {
      // Default to the first round if no active or selected round
      this.executeStartRound.set(rounds[0].number ?? 1);
    } else {
      // Fallback to 1
      this.executeStartRound.set(1);
    }

    this.isExecuteModalOpen.set(true);
  }

  openDryRunModal(definitionId: string): void {
    this.executeDefinitionId.set(definitionId);
    this.isDryRunMode.set(true);
    // Default to active/selected/first round like execute
    const selectedRound = this.tournamentsService.selectedRoundSignal();
    const rounds = this.activeTournamentRounds();
    const activeRound = rounds.find((r) => r.active);
    if (activeRound) {
      this.executeStartRound.set(activeRound.number ?? 1);
    } else if (selectedRound?.number) {
      this.executeStartRound.set(selectedRound.number);
    } else if (rounds.length > 0) {
      this.executeStartRound.set(rounds[0].number ?? 1);
    } else {
      this.executeStartRound.set(1);
    }
    this.isExecuteModalOpen.set(true);
  }

  closeExecuteModal(): void {
    this.isExecuteModalOpen.set(false);
    this.executeDefinitionId.set(null);
    this.isDryRunMode.set(false);
  }

  confirmExecution(): void {
    const definitionId = this.executeDefinitionId();
    const startRound = this.executeStartRound();

    if (!definitionId) return;

    this.closeExecuteModal();

    // CRITICAL: Reload definition from API to ensure we have latest continuousExecution flag
    // The cached definitions() signal may not reflect recent updates
    this.subscriptions.add(
      this.simulatorService.getDefinitionById(definitionId).subscribe({
        next: (def) => {
          console.log('[SimulatorComponent] Definition reloaded from API:', def);
          console.log('[SimulatorComponent] useMockTournament:', def.useMockTournament);

          this.executeDefinition(definitionId, startRound);
        },
        error: (err) => {
          console.error('[SimulatorComponent] Failed to reload definition:', err);
          this.toastr.error('Failed to load definition details');
        }
      })
    );
  }

  confirmDryRun(): void {
    const definitionId = this.executeDefinitionId();
    const startRound = this.executeStartRound();
    if (!definitionId) return;
    this.closeExecuteModal();
    const request = {
      definitionId,
      startAtRound: startRound,
      dryRun: true,
      dryRunValidation: {
        validateLegacyData: true,
        validateVNextPrerequisites: true,
        validateApiConnectivity: true,
        generatePreview: true,
        previewScoreCount: 10,
      },
    } as const;

    this.subscriptions.add(
      this.simulatorService.runDryRun(request as any).subscribe({
        next: (execution) => {
          // Expect status 'DryRunComplete' and results in execution.dryRunResults
          this.currentExecution.set(execution);
          this.executionProgress.set(null as any);
          this.showValidationResults.set(true);
          this.activeTab.set('execute');
          this.toastr.success('Dry run completed', 'Validation');
        },
        error: (err) => {
          console.error('Failed to run dry run:', err);
          this.toastr.error('Failed to run dry run', 'Error');
        },
      })
    );
  }

  executeDefinition(definitionId: string, startAtRound: number = 1): void {
    const request = {
      definitionId,
      startAtRound,
      dryRun: false,
    };

    // Reset progress tracking for new execution
    this.maxProgressPercentageReached.set(0);

    this.subscriptions.add(
      this.simulatorService.executeSimulation(request).subscribe({
        next: (execution) => {
          // Set the simulation context based on the definition's profile
          const definition = this.definitions().find(
            (d) => d.id === definitionId
          );
          if (definition?.profileName) {
            this.simulationContextService.setActiveProfile(
              definition.profileName
            );
          }

          // CRITICAL: Reload execution from API to get fresh status (creation response shows "Pending" 
          // but background service may have already changed it to "Running")
          this.subscriptions.add(
            this.simulatorService.getExecution(execution.id).subscribe({
              next: (freshExecution) => {
                this.currentExecution.set(freshExecution);
                this.startProgressMonitoring(freshExecution.id);
                this.activeTab.set('execute');
              },
              error: (err) => {
                console.error('Failed to reload execution after creation:', err);
                // Fallback to using the creation response
                this.currentExecution.set(execution);
                this.startProgressMonitoring(execution.id);
                this.activeTab.set('execute');
              }
            })
          );
        },
        error: (err) => {
          console.error('Failed to execute simulation:', err);
          alert('Failed to execute simulation. See console for details.');
        },
      })
    );
  }

  pauseExecution(): void {
    const executionId = this.currentExecution()?.id;
    if (!executionId) return;

    this.subscriptions.add(
      this.simulatorService.pauseExecution(executionId).subscribe({
        next: (execution) => {
          this.currentExecution.set(execution);
          this.toastr.info('Execution paused at next hole boundary');
        },
        error: (err) => {
          console.error('Failed to pause execution:', err);
          this.toastr.error('Failed to pause execution. See console for details.');
        },
      })
    );
  }

  resumeExecution(): void {
    const executionId = this.currentExecution()?.id;
    if (!executionId) return;

    this.subscriptions.add(
      this.simulatorService.resumeExecution(executionId).subscribe({
        next: (execution) => {
          this.currentExecution.set(execution);
          this.toastr.info('Execution resumed');

          // Use existing progress state as baseline - don't reload it
          // The execution object already has the correct paused state
          const existingProgress = this.executionProgress();
          if (existingProgress) {
            const currentProgress = existingProgress.progressPercentage || 0;
            this.maxProgressPercentageReached.set(currentProgress);
          }

          // Start progress monitoring from the existing baseline
          this.startProgressMonitoring(executionId);
        },
        error: (err) => {
          console.error('Failed to resume execution:', err);
          this.toastr.error('Failed to resume execution. See console for details.');
        },
      })
    );
  }

  async stopExecution(): Promise<void> {
    const executionId = this.currentExecution()?.id;
    if (!executionId) return;

    const confirmed = await this.dialogService.confirm(
      'Are you sure you want to stop this execution? This cannot be undone.',
      {
        title: 'Stop Simulation Execution',
        confirmLabel: 'Stop',
        cancelLabel: 'Cancel'
      }
    );

    if (!confirmed) {
      return;
    }

    this.subscriptions.add(
      this.simulatorService.stopExecution(executionId).subscribe({
        next: (execution) => {
          this.currentExecution.set(execution);
          this.simulatorService.stopProgressPolling();

          // Clear simulation context when manually stopped
          this.simulationContextService.clearActiveProfile();
        },
        error: (err) => {
          console.error('Failed to stop execution:', err);
          alert('Failed to stop execution. See console for details.');
        },
      })
    );
  }

  validateCurrentRound(): void {
    const executionId = this.currentExecution()?.id;
    const currentRound = this.executionProgress()?.currentRound;

    if (!executionId || !currentRound) {
      this.toastr.warning('No active round to validate', '', { positionClass: 'toast-top-right' });
      return;
    }

    this.subscriptions.add(
      this.simulatorService.validateRound(executionId, currentRound).subscribe({
        next: (validation) => {
          if (validation.isValid) {
            this.toastr.success(validation.validationMessage || 'All scores are complete', 'Validation Passed', { positionClass: 'toast-top-right' });
          } else {
            this.toastr.warning(validation.validationMessage || 'Some scores are incomplete', 'Validation Failed', { positionClass: 'toast-top-right' });
          }

          // Update the execution progress to show validation results
          const currentProgress = this.executionProgress();
          if (currentProgress) {
            this.executionProgress.set({
              ...currentProgress,
              lastRoundValidation: validation
            });
          }
        },
        error: (err) => {
          console.error('Failed to validate round:', err);
          this.toastr.error('Failed to validate round. See console for details.');
        },
      })
    );
  }

  compareLegacyReplayCurrentRound(): void {
    const executionId = this.currentExecution()?.id;
    const currentRound = this.executionProgress()?.currentRound;

    if (!executionId || !currentRound) {
      this.toastr.warning('No active round to compare', '', { positionClass: 'toast-top-right' });
      return;
    }

    this.toastr.info('Comparing legacy replay data...', 'Please wait', { positionClass: 'toast-top-right', timeOut: 3000 });

    this.subscriptions.add(
      this.simulatorService.compareLegacyReplay(executionId, currentRound).subscribe({
        next: (comparison) => {
          if (comparison.isValid) {
            this.toastr.success(
              comparison.validationMessage || `All ${comparison.totalPlayersCompared} players match exactly!`,
              'Legacy Replay Validated',
              { positionClass: 'toast-top-right', timeOut: 5000 }
            );
          } else {
            this.toastr.error(
              comparison.validationMessage || `${comparison.playersWithDiscrepancies} of ${comparison.totalPlayersCompared} players have discrepancies`,
              'Legacy Replay Validation Failed',
              { positionClass: 'toast-top-right', timeOut: 10000 }
            );
          }

          // Log detailed comparison results
          console.log('Legacy Replay Comparison Results:', comparison);
          if (comparison.playerDiscrepancies.length > 0) {
            console.table(comparison.playerDiscrepancies.map(p => ({
              Player: p.playerName || `ID ${p.playerId}`,
              ScoreMismatch: !p.scoreCounts.matches,
              ShotMismatch: !p.shotCounts.matches,
              Discrepancies: p.holeDiscrepancies.length
            })));
          }
        },
        error: (err) => {
          console.error('Failed to compare legacy replay:', err);
          this.toastr.error('Failed to compare legacy replay data. See console for details.', 'Comparison Error');
        },
      })
    );
  }

  compareLegacyReplayForExecution(executionId: string): void {
    this.subscriptions.add(
      this.simulatorService.getExecutionProgress(executionId).subscribe({
        next: (progress) => {
          const roundToCompare = progress.currentRound;
          if (!roundToCompare) {
            this.toastr.warning('No round information available for this execution', '', { positionClass: 'toast-top-right' });
            return;
          }

          this.toastr.info('Comparing legacy replay data...', 'Please wait', { positionClass: 'toast-top-right', timeOut: 3000 });

          this.subscriptions.add(
            this.simulatorService.compareLegacyReplay(executionId, roundToCompare).subscribe({
              next: (comparison) => {
                if (comparison.isValid) {
                  this.toastr.success(
                    comparison.validationMessage || `All ${comparison.totalPlayersCompared} players match exactly!`,
                    'Legacy Replay Validated',
                    { positionClass: 'toast-top-right', timeOut: 5000 }
                  );
                } else {
                  this.toastr.error(
                    comparison.validationMessage || `${comparison.playersWithDiscrepancies} of ${comparison.totalPlayersCompared} players have discrepancies`,
                    'Legacy Validation Failed',
                    { positionClass: 'toast-top-right', timeOut: 10000 }
                  );
                }

                // Log detailed comparison results
                console.log('Legacy Replay Comparison Results:', comparison);
                if (comparison.playerDiscrepancies.length > 0) {
                  console.table(comparison.playerDiscrepancies.map(p => ({
                    Player: p.playerName || `ID ${p.playerId}`,
                    ScoreMismatch: !p.scoreCounts.matches,
                    ShotMismatch: !p.shotCounts.matches,
                    Discrepancies: p.holeDiscrepancies.length
                  })));
                }
              },
              error: (err) => {
                console.error('Failed to compare legacy replay:', err);
                this.toastr.error('Failed to compare legacy replay data. See console for details.', 'Comparison Error');
              },
            })
          );
        },
        error: (err) => {
          console.error('Failed to get execution progress:', err);
          this.toastr.error('Failed to retrieve execution details.');
        },
      })
    );
  }

  async validateExecutionRound(executionId: string, roundNumber: number): Promise<void> {
    console.log('Validating round:', { executionId, roundNumber });
    this.toastr.info('Validating round data...', 'Please wait', { positionClass: 'toast-top-right', timeOut: 3000 });

    let execution = this.executionHistory().find((e: SimulationExecutionDto) => e.id === executionId);
    if (!execution) {
      execution = this.currentExecution() ?? undefined;
    }
    if (!execution) {
      this.toastr.error('No execution found');
      return;
    }

    const definition = this.getDefinition(execution.definitionId);
    if (!definition || !definition.vNextTournamentId) {
      this.toastr.error('Cannot validate: no vNext tournament ID');
      return;
    }

    // Check if we have cached validation data on the execution
    if (execution.validationDataJson) {
      try {
        const cachedValidation = JSON.parse(execution.validationDataJson);
        this.validationResult.set(cachedValidation);
        this.isValidationModalOpen.set(true);
        this.toastr.info('Showing previously cached validation results.', 'Cached Data', { positionClass: 'toast-top-right', timeOut: 5000 });
        return;
      } catch (parseError) {
        console.warn('Failed to parse cached validation data:', parseError);
      }
    }

    // Run fresh validation
    this.subscriptions.add(
      this.simulatorService.validateRound(executionId, roundNumber).subscribe({
        next: (validation) => {
          console.log('Validation result received:', validation);
          this.validationResult.set(validation);
          this.isValidationModalOpen.set(true);
        },
        error: (err) => {
          console.error('Failed to validate round:', err);
          this.toastr.error('Failed to validate round. See console for details.');
        },
      })
    );
  }

  async resetCurrentRound(): Promise<void> {
    const execution = this.currentExecution();
    if (!execution) {
      this.toastr.error('No execution found');
      return;
    }

    const roundNumber = execution.currentRound || 1;
    await this.resetRound(execution.id, roundNumber);
  }

  async resetRound(executionId: string, roundNumber: number): Promise<void> {
    const confirmed = await this.dialogService.confirm(
      `Are you sure you want to reset Round ${roundNumber}? This will permanently delete all scores and shots for this round, allowing it to be replayed.`,
      {
        title: 'Reset Round',
        confirmLabel: 'Reset Round',
        cancelLabel: 'Cancel',
        danger: true
      }
    );

    if (!confirmed) {
      return;
    }

    // Get execution and definition to find tournament and round IDs
    let execution = this.executionHistory().find((e: SimulationExecutionDto) => e.id === executionId);
    if (!execution) {
      execution = this.currentExecution() ?? undefined;
    }
    if (!execution) {
      this.toastr.error('No execution found');
      return;
    }

    const definition = this.getDefinition(execution.definitionId);
    if (!definition || !definition.vNextTournamentId) {
      this.toastr.error('Cannot reset: no vNext tournament ID');
      return;
    }

    console.log('Resetting round:', { executionId, roundNumber, tournamentId: definition.vNextTournamentId });

    // Track start time
    const startTime = Date.now();

    // Set loading state
    this.isResettingRound.set(true);

    // Get round ID from tournament service
    this.subscriptions.add(
      this.tournamentsService.getRounds(definition.vNextTournamentId).subscribe({
        next: (rounds) => {
          const round = rounds.find((r: Round) => r.number === roundNumber);

          if (!round || !round.roundId) {
            this.isResettingRound.set(false);
            this.toastr.error(`Round ${roundNumber} not found in tournament`);
            return;
          }

          console.log(`[Reset Round] Calling API to remove scores at ${Date.now() - startTime}ms`);

          // Call vNext API directly to remove scores
          this.subscriptions.add(
            this.scorecardService.removeScoresForRound(definition.vNextTournamentId!, round.roundId).subscribe({
              next: (response) => {
                const duration = ((Date.now() - startTime) / 1000).toFixed(1);
                console.log(`[Reset Round] Completed in ${duration}s - Removed ${response.scoresRemoved} scores from ${response.scorecardsAffected} scorecards`);

                this.isResettingRound.set(false);
                this.toastr.success(
                  `Removed ${response.scoresRemoved} scores from ${response.scorecardsAffected} scorecards.`,
                  `Round ${roundNumber} Reset Complete`,
                  { positionClass: 'toast-top-right', timeOut: 10000 }
                );
              },
              error: (err) => {
                const duration = ((Date.now() - startTime) / 1000).toFixed(1);
                console.error(`[Reset Round] Failed after ${duration}s:`, err);

                this.isResettingRound.set(false);
                this.toastr.error(
                  err.error?.message || 'Failed to reset round. See console for details.',
                  'Reset Failed',
                  { positionClass: 'toast-top-right', timeOut: 8000 }
                );
              },
            })
          );
        },
        error: (err) => {
          this.isResettingRound.set(false);
          console.error('Failed to get tournament rounds:', err);
          this.toastr.error('Failed to get tournament information. See console for details.');
        },
      })
    );
  }

  async restoreDatabase(): Promise<void> {
    // First, get preview to show user what will happen
    this.subscriptions.add(
      this.simulatorService.getDatabaseRestorePreview().subscribe({
        next: async (preview) => {
          const options = await this.dialogService.confirmRestoreDatabase({
            preview,
            allowRestoreScoring: preview.dacpacExists
          });

          if (!options) {
            return;
          }

          // Proceed with restore
          this.isRestoringDatabase.set(true);
          const startTime = Date.now();

          this.subscriptions.add(
            this.simulatorService.restoreDatabase(options, 'User initiated from Admin UI').subscribe({
              next: (result) => {
                const duration = ((Date.now() - startTime) / 1000).toFixed(1);
                console.log(`[Restore Database] Completed in ${duration}s - Database restored to version ${result.dacpacVersion}`);

                this.isRestoringDatabase.set(false);

                const messages: string[] = [];
                if (options.restoreScoring) {
                  messages.push(`Scoring databases restored (${result.dacpacVersion})`);
                }
                if (result.simulatorDataCleared) {
                  messages.push('Simulator data cleared');
                }

                this.toastr.success(
                  `${messages.join('. ')}. Duration: ${result.duration}`,
                  'Restore Complete',
                  { positionClass: 'toast-top-right', timeOut: 5000 }
                );

                setTimeout(() => {
                  if (options.restoreScoring) {
                    window.location.href = '/tournaments';
                    return;
                  }

                  this.refreshSimulatorState();
                }, 3000);
              },
              error: (err) => {
                const duration = ((Date.now() - startTime) / 1000).toFixed(1);
                console.error(`[Restore Database] Failed after ${duration}s:`, err);

                this.isRestoringDatabase.set(false);

                let errorMessage = 'Failed to restore database. See console for details.';
                if (err.error?.error) {
                  errorMessage = err.error.error;
                } else if (err.error?.message) {
                  errorMessage = err.error.message;
                } else if (typeof err.error === 'string') {
                  errorMessage = err.error;
                }

                this.toastr.error(
                  errorMessage,
                  'Restore Failed',
                  { positionClass: 'toast-top-right', timeOut: 8000 }
                );
              },
            })
          );
        },
        error: (err) => {
          console.error('Failed to get database restore preview:', err);
          this.toastr.error(
            'Failed to get database restore information. Is the Simulator API running?',
            'Preview Failed',
            { positionClass: 'toast-top-right', timeOut: 10000 }
          );
        },
      })
    );
  }

  closeValidationModal(): void {
    this.isValidationModalOpen.set(false);
    this.validationResult.set(null);
  }

  getDefinition(definitionId: string | undefined): SimulationDefinitionDto | null {
    if (!definitionId) return null;
    return this.definitions().find((d) => d.id === definitionId) || null;
  }

  refreshProgress(): void {
    const executionId = this.currentExecution()?.id;
    if (!executionId) return;

    this.subscriptions.add(
      this.simulatorService.getExecutionProgress(executionId).subscribe({
        next: (progress) => {
          // Enforce monotonic progress: never allow it to decrease
          this.updateProgressMonotonically(progress);
        },
        error: (err) => {
          console.error('Failed to refresh progress:', err);
        },
      })
    );
  }

  private startProgressMonitoring(executionId: string): void {
    // Stop any existing polling
    this.simulatorService.stopProgressPolling();

    // Start new polling
    this.subscriptions.add(
      this.simulatorService.startProgressPolling(executionId, 2000).subscribe({
        next: (progress) => {
          // Enforce monotonic progress: never allow it to decrease
          this.updateProgressMonotonically(progress);

          // Handle pending round transition (continuous execution mode)
          if (progress.roundTransitionStatus === 'Pending' && !this.isProcessingRoundTransition()) {
            this.handlePendingRoundTransition(executionId, progress);
          }

          // Update currentExecution with latest status from progress
          const current = this.currentExecution();
          if (current && current.id === executionId) {
            const previousStatus = current.status;
            this.currentExecution.set({
              ...current,
              status: progress.status,
            });

            // Force full refresh when transitioning from Pending to Running
            if (previousStatus === 'Pending' && progress.status === 'Running') {
              this.subscriptions.add(
                this.simulatorService.getExecution(executionId).subscribe({
                  next: (execution) => {
                    this.currentExecution.set(execution);
                  },
                  error: (err) => {
                    console.error(
                      'Failed to fetch execution on status change:',
                      err
                    );
                  },
                })
              );
            }
          }

          // Update execution with latest status
          if (
            progress.status === 'Completed' ||
            progress.status === 'Failed' ||
            progress.status === 'Stopped'
          ) {
            this.simulatorService.stopProgressPolling();

            // Clear simulation context when execution ends
            this.simulationContextService.clearActiveProfile();

            // Fetch final execution state
            this.subscriptions.add(
              this.simulatorService.getExecution(executionId).subscribe({
                next: (execution) => {
                  this.currentExecution.set(execution);
                },
                error: (err) => {
                  console.error('Failed to fetch final execution state:', err);
                },
              })
            );
          }
        },
        error: (err) => {
          console.error('Progress polling error:', err);
        },
      })
    );
  }

  private updateProgressMonotonically(
    progress: SimulationExecutionProgressDto
  ): void {
    // Enforce monotonic progress: ensure progress percentage never decreases
    const currentProgressPercent = progress.progressPercentage || 0;
    const maxSoFar = this.maxProgressPercentageReached();

    // Use the maximum of current and previously reached progress
    const monotonicallyIncreasingProgress = Math.max(
      currentProgressPercent,
      maxSoFar
    );

    // Update the max reached value
    this.maxProgressPercentageReached.set(monotonicallyIncreasingProgress);

    // Update the displayed progress with the monotonically increasing value
    this.executionProgress.set({
      ...progress,
      progressPercentage: monotonicallyIncreasingProgress,
    });
  }

  /**
   * Load progress once without polling (used for Paused executions)
   */
  private loadProgressOnce(executionId: string): void {
    this.subscriptions.add(
      this.simulatorService.getExecutionProgress(executionId).subscribe({
        next: (progress) => {
          this.updateProgressMonotonically(progress);
        },
        error: (err) => {
          console.error('Failed to load progress for paused execution:', err);
        },
      })
    );
  }

  // ===== Health & Status Checks =====

  viewExecutionSummary(executionId: string): void {
    this.summaryError.set(null);
    this.summaryLoading.set(true);
    this.isSummaryModalOpen.set(true);
    this.executionSummary.set(null);

    this.simulatorService.getExecutionSummary(executionId).subscribe({
      next: (summary) => {
        this.executionSummary.set(summary);
        this.summaryLoading.set(false);
      },
      error: (err) => {
        console.error('Failed to get execution summary:', err);
        this.summaryLoading.set(false);
        this.summaryError.set(
          err?.error?.message || 'Failed to retrieve execution summary'
        );
        this.toastr.error(
          err?.error?.message || 'Failed to retrieve execution summary',
          'Error'
        );
      },
    });
  }

  isCurrentRound(roundNumber: number, summary: SimulationExecutionSummaryDto): boolean {
    // Highlight the round that corresponds to the current execution
    // Since metadata.endRound represents the round this execution handled
    return roundNumber === summary.metadata.endRound;
  }

  closeSummaryModal(): void {
    this.isSummaryModalOpen.set(false);
    this.summaryLoading.set(false);
    this.summaryError.set(null);
    this.executionSummary.set(null);
  }

  statusBadgeClass(status: string | undefined): string {
    switch (status) {
      case 'Completed':
        return 'bg-success';
      case 'Running':
      case 'Pending':
        return 'bg-info';
      case 'Paused':
        return 'bg-warning text-dark';
      case 'Failed':
      case 'Stopped':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  getStatusIcon(status: string | undefined): string {
    switch (status) {
      case 'Completed':
        return 'fa-check-circle';
      case 'Running':
        return 'fa-spinner fa-spin';
      case 'Pending':
        return 'fa-hourglass-start';
      case 'Paused':
        return 'fa-pause-circle';
      case 'Failed':
        return 'fa-exclamation-circle';
      case 'Stopped':
        return 'fa-stop-circle';
      default:
        return 'fa-circle-o';
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Completed':
        return 'label-primary';
      case 'Running':
        return 'label-info';
      case 'Pending':
        return 'label-warning';
      case 'Failed':
        return 'label-danger';
      case 'Stopped':
        return 'label-default';
      default:
        return 'label-default';
    }
  }

  getDefinitionName(definitionId: string): string {
    const definition = this.definitions().find((d) => d.id === definitionId);
    return definition?.name || 'Unknown';
  }

  refreshExecutionHistory(): void {
    this.simulatorService.getAllExecutions().subscribe({
      next: (executions) => {
        const sortedExecs = [...executions].sort((a, b) => {
          const dateA = a.startedAt ? new Date(a.startedAt).getTime() : 0;
          const dateB = b.startedAt ? new Date(b.startedAt).getTime() : 0;
          return dateB - dateA;
        });
        this.executionHistory.set(sortedExecs);
      },
      error: (err) => {
        console.error('Failed to refresh execution history:', err);
      },
    });
  }

  setSummaryTab(
    tabName:
      | 'metadata'
      | 'performance'
      | 'errors'
      | 'rounds'
      | 'time-aware'
      | 'validation'
  ): void {
    this.summaryActiveTab.set(tabName);
  }

  formatLocalTime(utcTimestamp: string | undefined): string {
    if (!utcTimestamp) return '';
    // Append 'Z' to indicate UTC if not present, then convert to local
    const utcDate = utcTimestamp.endsWith('Z')
      ? new Date(utcTimestamp)
      : new Date(utcTimestamp + 'Z');
    return utcDate.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    });
  }

  formatDuration(startTime: string, endTime: string): string {
    const start = new Date(startTime).getTime();
    const end = new Date(endTime).getTime();
    const durationMs = end - start;

    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days}d ${hours % 24}h ${minutes % 60}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  }

  formatDurationTimeSpan(timeSpanString: string | undefined): string {
    if (!timeSpanString) return '—';

    // Parse TimeSpan format: "d.HH:mm:ss.fffffff" or "HH:mm:ss.fffffff" or "Xd HH:MM:SS"
    // Also handle the new "Xd HH:MM:SS" format from API
    const dayMatch = timeSpanString.match(/^(\d+)d\s+(\d+):(\d+):(\d+)/);
    if (dayMatch) {
      const days = parseInt(dayMatch[1], 10);
      const hours = parseInt(dayMatch[2], 10);
      const minutes = parseInt(dayMatch[3], 10);
      return `${days}d ${hours}h ${minutes}m`;
    }

    // Check for .NET TimeSpan format with days: "d.HH:mm:ss"
    const dotDayMatch = timeSpanString.match(/^(\d+)\.(\d+):(\d+):(\d+)/);
    if (dotDayMatch) {
      const days = parseInt(dotDayMatch[1], 10);
      const hours = parseInt(dotDayMatch[2], 10);
      const minutes = parseInt(dotDayMatch[3], 10);
      return `${days}d ${hours}h ${minutes}m`;
    }

    const parts = timeSpanString.split(':');
    if (parts.length < 3) return timeSpanString;

    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const secondsWithMs = parseFloat(parts[2]);
    const seconds = Math.floor(secondsWithMs);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  getExecutionDuration(execution: SimulationExecutionDto): string {
    if (execution.startedAt && execution.completedAt) {
      return this.formatDuration(execution.startedAt, execution.completedAt);
    } else if (execution.status === 'Running' && execution.startedAt) {
      return (
        this.formatDuration(execution.startedAt, new Date().toISOString()) +
        ' (running)'
      );
    }
    return '-';
  }

  private checkActiveTournament(): void {
    this.subscriptions.add(
      this.tournamentsService.getTournamentsEndpoint().subscribe({
        next: (
          tournaments: ModulesTournamentsApplicationTournamentsTournamentResponse[]
        ) => {
          this.hasActiveTournament.set(
            Array.isArray(tournaments) &&
            tournaments.some((t) => !!t.active || t.status === 'Active')
          );
        },
        error: () => {
          this.hasActiveTournament.set(false);
        },
      })
    );
  }

  private checkSimulatorStatus(): void {
    this.checkingStatus.set(true);
    this.simulatorStatus.set('unknown');
    this.subscriptions.add(
      this.simulatorService.checkHealth().subscribe({
        next: (health) => {
          this.simulatorStatus.set(
            health.status === 'Healthy' ? 'online' : 'offline'
          );
          this.checkingStatus.set(false);
        },
        error: () => {
          this.simulatorStatus.set('offline');
          this.checkingStatus.set(false);
        },
      })
    );
  }

  private loadActiveTournamentId(): void {
    this.subscriptions.add(
      this.tournamentsService.getTournamentsEndpoint().subscribe({
        next: (
          tournaments: ModulesTournamentsApplicationTournamentsTournamentResponse[]
        ) => {
          const activeTournament = tournaments.find(
            (t) => !!t.active || t.status === 'Active'
          );
          if (activeTournament?.tournamentId) {
            this.activeTournamentId.set(activeTournament.tournamentId);
            this.activeTournamentName.set(activeTournament.name || null);
            // Load rounds for the active tournament
            this.loadActiveTournamentRounds(activeTournament.tournamentId);
          } else {
            this.activeTournamentId.set(null);
            this.activeTournamentName.set(null);
            this.activeTournamentRounds.set([]);
            this.availableRoundNumbers.set([]);
          }
        },
        error: () => {
          this.activeTournamentId.set(null);
          this.activeTournamentName.set(null);
          this.activeTournamentRounds.set([]);
          this.availableRoundNumbers.set([]);
        },
      })
    );
  }

  private loadActiveTournamentRounds(tournamentId: string): void {
    this.subscriptions.add(
      this.tournamentsService.getRounds(tournamentId).subscribe({
        next: (rounds: Round[]) => {
          this.activeTournamentRounds.set(rounds);
          // Create array of round numbers [1, 2, 3, 4] based on available rounds
          const roundNumbers = rounds
            .map((r) => r.number)
            .filter((n) => n !== undefined && n !== null) as number[];
          roundNumbers.sort((a, b) => a - b);
          this.availableRoundNumbers.set(roundNumbers);
        },
        error: (err) => {
          console.error('Failed to load active tournament rounds:', err);
          this.activeTournamentRounds.set([]);
          this.availableRoundNumbers.set([]);
        },
      })
    );
  }

  private scrollModalToTop(): void {
    // Ensure modal container scrolls to top after it renders
    setTimeout(() => {
      const modalContainer = document.querySelector('.sim-modal');
      if (modalContainer) {
        modalContainer.scrollTop = 0;
      }
      // Place focus on the first input for accessibility without scrolling the page
      const firstField = document.querySelector(
        '.sim-modal .modal-body input, .sim-modal .modal-body select, .sim-modal .modal-body textarea'
      ) as HTMLElement | null;
      if (firstField && typeof firstField.focus === 'function') {
        firstField.focus({ preventScroll: true } as any);
      }
    }, 0);
  }

  /**
   * Handle a pending round transition during continuous execution.
   * This performs the round closure and activation in the vNext API,
   * then acknowledges the transition to the Simulator API to resume execution.
   */
  private handlePendingRoundTransition(
    executionId: string,
    progress: SimulationExecutionProgressDto
  ): void {
    // Prevent re-entry while processing
    if (this.isProcessingRoundTransition()) {
      return;
    }

    // Extract completed round number from the transition message
    // Expected format: "Round X completed, waiting for transition"
    const completedRound = progress.currentRound;
    const nextRoundNumber = completedRound + 1;

    // Get the definition to find the tournament ID
    const definition = this.selectedDefinition();
    if (!definition?.vNextTournamentId) {
      console.error('No vNext tournament ID found for round transition');
      this.roundTransitionMessage.set('Error: No tournament ID configured');
      return;
    }

    this.isProcessingRoundTransition.set(true);
    this.roundTransitionMessage.set(`Loading rounds for tournament...`);

    const tournamentId = definition.vNextTournamentId;

    // Fetch rounds for the definition's tournament (don't rely on activeTournamentRounds)
    this.tournamentsService.getRounds(tournamentId).subscribe({
      next: (rounds: Round[]) => {
        const currentRoundObj = rounds.find(r => r.number === completedRound);
        const nextRoundObj = rounds.find(r => r.number === nextRoundNumber);

        if (!currentRoundObj?.roundId) {
          console.error(`Current round ${completedRound} not found in tournament rounds`);
          this.roundTransitionMessage.set(`Error: Round ${completedRound} not found`);
          this.isProcessingRoundTransition.set(false);
          return;
        }

        this.roundTransitionMessage.set(`Closing round ${completedRound} and activating round ${nextRoundNumber}...`);
        this.toastr.info(`Performing round transition: Round ${completedRound} → Round ${nextRoundNumber}`, 'Continuous Execution');

        const currentRoundId = currentRoundObj.roundId;

        // Step 1: Close the current round with force=true (auto-marks scorecards official)
        this.performRoundTransition(executionId, tournamentId, completedRound, currentRoundId, nextRoundNumber, nextRoundObj?.roundId, definition.useMockTournament);
      },
      error: (err) => {
        console.error('Failed to load tournament rounds for transition:', err);
        this.roundTransitionMessage.set('Error: Failed to load tournament rounds');
        this.isProcessingRoundTransition.set(false);
        this.toastr.error('Failed to load tournament rounds for transition', 'Error');
      }
    });
  }

  /**
   * Perform the actual round transition after rounds have been loaded.
   * For Mock mode, this includes copying pairings from the completed round to the next round.
   */
  private async performRoundTransition(
    executionId: string,
    tournamentId: string,
    completedRound: number,
    currentRoundId: string,
    nextRoundNumber: number,
    nextRoundId: string | undefined,
    isMockMode: boolean
  ): Promise<void> {
    try {
      // Step 1: Close the current round with force=true (auto-marks scorecards official)
      await firstValueFrom(this.tournamentsService.closeRound(tournamentId, currentRoundId, true));
      console.log(`Round ${completedRound} closed successfully`);

      // Step 2: Check if there's a next round to activate
      if (!nextRoundId) {
        // No next round - tournament is complete
        console.log('No next round found. Tournament simulation complete.');
        this.roundTransitionMessage.set('Tournament simulation complete - no more rounds');
        this.toastr.success('Tournament simulation completed!', 'Continuous Execution');

        // Acknowledge as success but with flag that there's no next round
        await firstValueFrom(this.simulatorService.acknowledgeRoundTransition(
          executionId,
          true,
          0, // No next round
          'Tournament complete - no more rounds'
        ));
        this.isProcessingRoundTransition.set(false);
        this.roundTransitionMessage.set(null);
        return;
      }

      // Step 3: For Mock mode, copy pairings from completed round to next round
      if (isMockMode) {
        this.roundTransitionMessage.set(`Copying pairings from round ${completedRound} to round ${nextRoundNumber}...`);

        try {
          // Get the round tournament courses for both rounds
          const [currentRoundCourses, nextRoundCourses] = await Promise.all([
            firstValueFrom(this.pairingsService.getCoursesSnapshotForRound(currentRoundId)),
            firstValueFrom(this.pairingsService.getCoursesSnapshotForRound(nextRoundId))
          ]);

          if (currentRoundCourses.length === 0) {
            throw new Error(`No courses found for completed round ${completedRound}`);
          }
          if (nextRoundCourses.length === 0) {
            throw new Error(`No courses found for next round ${nextRoundNumber}`);
          }

          // Copy pairings for each matching course
          for (const sourceCourse of currentRoundCourses) {
            const targetCourse = nextRoundCourses.find(c => c.tournamentCourseId === sourceCourse.tournamentCourseId);
            if (targetCourse && sourceCourse.roundTournamentCourseId && targetCourse.roundTournamentCourseId) {
              console.log(`Copying pairings from course ${sourceCourse.roundTournamentCourseId} to ${targetCourse.roundTournamentCourseId}`);
              await firstValueFrom(this.pairingsService.copyPairings(
                sourceCourse.roundTournamentCourseId,
                targetCourse.roundTournamentCourseId
              ));
            }
          }

          console.log('Pairings copied successfully for Mock mode');
          this.toastr.info('Pairings copied from previous round', 'Continuous Execution');
        } catch (copyErr: any) {
          console.error('Failed to copy pairings for Mock mode:', copyErr);
          this.roundTransitionMessage.set(`Error copying pairings: ${copyErr.message || 'Unknown error'}`);
          this.toastr.error(`Failed to copy pairings: ${copyErr.message || 'Unknown error'}`, 'Error');

          // Acknowledge as failure
          await firstValueFrom(this.simulatorService.acknowledgeRoundTransition(
            executionId,
            false,
            nextRoundNumber,
            `Failed to copy pairings: ${copyErr.message || 'Unknown error'}`
          ));
          this.isProcessingRoundTransition.set(false);
          return;
        }
      }

      // Step 4: Activate the next round
      this.roundTransitionMessage.set(`Activating round ${nextRoundNumber}...`);
      await firstValueFrom(this.tournamentsService.activateRound(tournamentId, nextRoundId));
      console.log(`Round ${nextRoundNumber} activated successfully`);
      this.roundTransitionMessage.set(`Round ${nextRoundNumber} activated. Resuming simulation...`);
      this.toastr.success(`Round ${nextRoundNumber} activated. Simulation resuming...`, 'Continuous Execution');

      // Step 5: Acknowledge the transition to the Simulator API
      await firstValueFrom(this.simulatorService.acknowledgeRoundTransition(
        executionId,
        true,
        nextRoundNumber,
        `Successfully transitioned from round ${completedRound} to round ${nextRoundNumber}`
      ));
      console.log('Round transition acknowledged successfully');
      this.isProcessingRoundTransition.set(false);
      this.roundTransitionMessage.set(null);
      // Reset max progress for the new round
      this.maxProgressPercentageReached.set(0);

    } catch (err: any) {
      console.error('Round transition failed:', err);
      this.roundTransitionMessage.set(`Error: ${err.message || 'Round transition failed'}`);
      this.toastr.error(`Round transition failed: ${err.message || 'Unknown error'}`, 'Error');

      // Acknowledge as failure
      try {
        await firstValueFrom(this.simulatorService.acknowledgeRoundTransition(
          executionId,
          false,
          nextRoundNumber,
          `Round transition failed: ${err.message || 'Unknown error'}`
        ));
      } catch {
        // Ignore acknowledgement errors
      }
      this.isProcessingRoundTransition.set(false);
    }
  }
}

